import { useEffect } from "react";
import api from "../lib/api";

/**
 * On native (Capacitor) only: ask for push permission, register with APNs/FCM,
 * and POST the token to the backend so we can deliver pushes to this device.
 *
 * No-op in browsers — web push is handled separately via the service worker
 * and `NotificationsContext.enableBrowserPush()`.
 */
export default function useNativePush() {
  useEffect(() => {
    const isNative = typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.();
    if (!isNative) return;

    let registrationListener;
    let errorListener;

    (async () => {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");

        let status = await PushNotifications.checkPermissions();
        if (status.receive === "prompt" || status.receive === "prompt-with-rationale") {
          status = await PushNotifications.requestPermissions();
        }
        if (status.receive !== "granted") {
          console.info("Native push permission not granted");
          return;
        }

        registrationListener = await PushNotifications.addListener("registration", async (token) => {
          const platform = window.Capacitor?.getPlatform?.() === "ios" ? "ios" : "android";
          try {
            await api.post("/push/native/register", {
              platform,
              token: token.value,
            });
          } catch (e) {
            console.warn("Failed to register native push token with backend:", e);
          }
        });

        errorListener = await PushNotifications.addListener("registrationError", (err) => {
          console.warn("Native push registration error:", err);
        });

        await PushNotifications.register();
      } catch (e) {
        console.warn("Native push setup failed:", e);
      }
    })();

    return () => {
      registrationListener?.remove?.();
      errorListener?.remove?.();
    };
  }, []);
}
